import React, { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import logo from "../../assets/images/logo_wevalo.png"
import { get } from "../../helpers/api_helper"
import { getUserRoleFromToken } from "../../helpers/auth_token_helper"
import AdminUserForm from "./components/AdminUserForm"
import RegistrationRecoveryStep from "./components/RegistrationRecoveryStep"
import EmailVerificationStep from "./components/EmailVerificationStep"
import StoreConfirmationForm from "./components/StoreConfirmationForm"
import StoreSearchStep from "./components/StoreSearchStep"
import { usePartnerSignupApi } from "./hooks/usePartnerSignupApi"
import { getEmptyStoreValues, getStoreId, mapGooglePlaceToStore } from "./partnerSignupUtils"
import { getPartnerRegistrationConflictData } from "../../services/partnerSignupService"
import "./partner-signup.scss"

const emptyAdmin = {
  email: "",
  fullName: "",
  password: "",
  passwordConfirmation: "",
}

const PartnerSignupPage = () => {
  const navigate = useNavigate()
  const api = usePartnerSignupApi()
  const [step, setStep] = useState(1)
  const [isManualEntry, setIsManualEntry] = useState(false)
  const [storeValues, setStoreValues] = useState(null)
  const [registrationToken, setRegistrationToken] = useState("")
  const [adminValues, setAdminValues] = useState(emptyAdmin)
  const [flowError, setFlowError] = useState("")
  const [recoveryConflict, setRecoveryConflict] = useState(null)

  useEffect(() => {
    document.title = "Inscription partenaire | Wevalo"
    document.body.classList.add("partner-signup-body")
    return () => document.body.classList.remove("partner-signup-body")
  }, [])

  const clearErrors = () => {
    setFlowError("")
    api.clearError()
  }

  const handlePlaceSelected = place => {
    clearErrors()
    setIsManualEntry(false)
    setRecoveryConflict(null)
    setStoreValues(mapGooglePlaceToStore(place))
    setStep(2)
  }

  const handleManualEntry = () => {
    clearErrors()
    setIsManualEntry(true)
    setRecoveryConflict(null)
    setStoreValues(getEmptyStoreValues(true))
    setStep(2)
  }

  const handleStoreSubmit = async values => {
    clearErrors()
    setStoreValues(values)
    try {
      const store = await api.createStore(values)
      const newStoreId = getStoreId(store)
      if (!newStoreId) {
        setFlowError("Le commerce a été créé, mais son identifiant n’a pas été retourné par l’API. Contactez le support.")
        return
      }
      if (!store.registrationToken) {
        setFlowError("Le commerce a été créé, mais le token d’inscription partenaire n’a pas été retourné par l’API. Contactez le support.")
        return
      }
      setRegistrationToken(store.registrationToken)
      if (values.profilePhotoFile) {
        try {
          await api.uploadStoreProfilePhoto(newStoreId, values.profilePhotoFile, store.registrationToken)
        } catch {
          setFlowError("Le commerce a été créé, mais la photo de profil n’a pas pu être envoyée. Vous pourrez l’ajouter plus tard depuis les paramètres.")
        }
      }
      setStep(3)
    } catch (error) {
      const conflict = getPartnerRegistrationConflictData(error)
      if (conflict) {
        setRecoveryConflict(conflict)
        if (conflict.registrationToken) setRegistrationToken(conflict.registrationToken)
        if (conflict.email) setAdminValues(current => ({ ...current, email: conflict.email }))
        api.clearError()
        setStep(5)
      }
    }
  }

  const handleAdminSubmit = async values => {
    clearErrors()
    setAdminValues(values)
    if (!registrationToken) {
      setFlowError("Votre session d’inscription partenaire est invalide. Recommencez la création du commerce.")
      return
    }
    try {
      await api.createAdmin(values, registrationToken)
      setRegistrationToken("")
      setStep(4)
    } catch (error) {
      if (getPartnerRegistrationConflictData(error)) {
        api.clearError()
        setStep(4)
      }
    }
  }

  const handleResend = async () => {
    clearErrors()
    try {
      if (recoveryConflict?.userExists && recoveryConflict.storeId) {
        await api.resendEmailByStore(recoveryConflict.storeId)
      } else {
        await api.resendEmail(adminValues.email)
      }
      return true
    } catch {
      return false
    }
  }

  const handleRecoveryCreateAdmin = () => {
    clearErrors()
    if (!recoveryConflict?.registrationToken) return
    setRegistrationToken(recoveryConflict.registrationToken)
    setStep(3)
  }

  const handleRecoveryVerifyEmail = async email => {
    clearErrors()
    try {
      if (recoveryConflict?.userExists) {
        if (!recoveryConflict.storeId) {
          setFlowError("L’identifiant du commerce n’a pas été retourné par l’API. Contactez le support.")
          return
        }
        await api.resendEmailByStore(recoveryConflict.storeId)
      } else {
        await api.resendEmail(email)
        setAdminValues(current => ({ ...current, email, password: "", passwordConfirmation: "" }))
      }
      setStep(4)
    } catch {
      // Le hook expose le message API dans l’interface.
    }
  }

  const handleRestart = () => {
    clearErrors()
    setRecoveryConflict(null)
    setRegistrationToken("")
    setAdminValues(emptyAdmin)
    setStep(1)
  }

  const handleActivateUser = async verificationCode => {
    clearErrors()
    try {
      if (recoveryConflict?.userExists) {
        if (!recoveryConflict.storeId) {
          setFlowError("L’identifiant du commerce n’a pas été retourné par l’API. Contactez le support.")
          return
        }
        await api.activateUserByStore(recoveryConflict.storeId, verificationCode)
      } else {
        await api.activateUser(adminValues.email, verificationCode)
      }
      if (!adminValues.password) {
        navigate("/login", { state: { accountActivated: true } })
        return
      }
      const login = await api.loginPartner(adminValues.email, adminValues.password)
      if (!login?.token) {
        setFlowError("Votre compte est activé, mais aucun token de connexion n’a été retourné. Connectez-vous depuis la page de connexion.")
        return
      }

      localStorage.setItem("authUser", login.token)
      const responseStoreDetail = await get("store/mine")
      if (responseStoreDetail?.status === 200) {
        localStorage.setItem("storeDetail", JSON.stringify({
          name: responseStoreDetail.data.name,
          profilePhoto: responseStoreDetail.data.profilePhoto,
        }))
      }

      const role = getUserRoleFromToken()
      navigate(role === "admin" || role === "manager" ? "/dashboard" : "/sales")
    } catch {
      // Le hook expose le message d’activation ou de connexion dans l’interface.
    }
  }

  const currentError = flowError || api.error

  return (
    <main className={`partner-signup-shell partner-signup-screen-${step}`}>
      <header className="partner-signup-header">
        <Link to="/" className="partner-brand" aria-label="Wevalo">
          <img src={logo} alt="Wevalo" />
        </Link>
        <div>
          <span>Déjà partenaire ?</span>
          <Link to="/login">Se connecter</Link>
        </div>
      </header>

      <div className="partner-signup-container">
        {step === 1 && <StoreSearchStep onPlaceSelected={handlePlaceSelected} onManualEntry={handleManualEntry} />}
        {step === 2 && storeValues && (
          <StoreConfirmationForm
            initialValues={storeValues}
            isManualEntry={isManualEntry}
            onBack={() => {
              clearErrors()
              setStep(1)
            }}
            onSubmit={handleStoreSubmit}
            loading={api.loading}
            apiError={currentError}
          />
        )}
        {step === 3 && (
          <AdminUserForm
            initialValues={adminValues}
            onSubmit={handleAdminSubmit}
            loading={api.loading}
            apiError={currentError}
          />
        )}
        {step === 4 && (
          <EmailVerificationStep
            email={adminValues.email}
            onResend={handleResend}
            onActivate={handleActivateUser}
            loading={api.loading}
            apiError={currentError}
            autoLogin={Boolean(adminValues.password)}
          />
        )}
        {step === 5 && recoveryConflict && (
          <RegistrationRecoveryStep
            conflict={recoveryConflict}
            initialEmail={adminValues.email}
            onCreateAdmin={handleRecoveryCreateAdmin}
            onVerifyEmail={handleRecoveryVerifyEmail}
            onRestart={handleRestart}
            loading={api.loading}
            apiError={currentError}
          />
        )}
        <footer className="partner-signup-footer">
          <span><i className="bx bx-shield-quarter" /> Données sécurisées</span>
          <span>Besoin d’aide ? <a href="mailto:support@wevalo.com">support@wevalo.com</a></span>
        </footer>
      </div>
    </main>
  )
}

export default PartnerSignupPage
